import { jwtDecode } from 'jwt-decode';
import { authApi, getTokenDeliveryMode, isRefreshBlocked, RefreshBlockedError, singleFlightRefresh, } from '@digiwedge/idp-client';
import { clearCookieAuthPending, hasCookieAuthPending } from './cookie-auth.js';
import { readCookie, readResponseHeader } from './csrf-token.js';
const SKEW_MS = 90_000;
function now() {
    return Date.now();
}
function resolveAuthTarget(path) {
    const internals = authApi;
    const base = (internals.basePath ?? '').replace(/\/+$/, '');
    return base ? `${base}${path}` : path;
}
function isCookieAuthOk(value) {
    return typeof value === 'object' && value !== null && value.ok === true;
}
export function clearStoredTokens() {
    try {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('jti');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('jti');
    }
    catch {
        /* ignore */
    }
}
export function decodeExp(token) {
    try {
        const p = jwtDecode(token);
        return (p?.exp ?? 0) * 1000;
    }
    catch {
        return 0;
    }
}
export function persist(tokens, remember) {
    if (getTokenDeliveryMode() === 'cookie') {
        clearStoredTokens();
        return;
    }
    const store = remember ? localStorage : sessionStorage;
    const clear = remember ? sessionStorage : localStorage;
    store.setItem('accessToken', tokens.accessToken);
    if (tokens.refreshToken)
        store.setItem('refreshToken', tokens.refreshToken);
    else
        store.removeItem('refreshToken');
    if (tokens.jti)
        store.setItem('jti', tokens.jti);
    else
        store.removeItem('jti');
    clear.removeItem('accessToken');
    clear.removeItem('refreshToken');
    clear.removeItem('jti');
}
async function fetchCsrfToken() {
    try {
        const response = await fetch(resolveAuthTarget('/api/auth/csrf'), {
            credentials: 'include',
            cache: 'no-store',
        });
        return readResponseHeader(response.headers, 'x-xsrf-token') ?? readCookie('XSRF-TOKEN');
    }
    catch {
        return null;
    }
}
export async function refreshWithCsrf(audience, body, prefetchedXsrfToken) {
    if (isRefreshBlocked())
        return { failed: true, transient: false };
    const cookieOnly = getTokenDeliveryMode() === 'cookie';
    const internals = authApi;
    const basePath = (internals.basePath ?? '').replace(/\/+$/, '');
    const xsrfToken = readCookie('XSRF-TOKEN') ??
        (prefetchedXsrfToken !== undefined ? prefetchedXsrfToken : await fetchCsrfToken());
    let res;
    try {
        res = await singleFlightRefresh({
            basePath,
            tokenDelivery: cookieOnly ? 'cookie' : 'json',
            audience,
            xsrfToken,
            refreshPayload: cookieOnly ? undefined : body,
        });
    }
    catch (err) {
        if (err instanceof RefreshBlockedError) {
            return { failed: true, transient: false };
        }
        // Network failure / thrown fetch — classify as transient; the guard
        // is intentionally NOT set for thrown errors by the coordinator.
        return { failed: true, transient: true };
    }
    // Auth-boot–local parsing: convert the raw Response to
    // Tokens | { ok: true } | RefreshFailure. No other caller sees this shape.
    if (!res.ok) {
        const transient = res.status >= 500 || res.status === 429;
        return { failed: true, transient };
    }
    const data = (await res.json());
    if (isCookieAuthOk(data))
        return { ok: true };
    if (!data?.accessToken)
        return { failed: true, transient: false };
    return {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        jti: data.jti,
        exp: decodeExp(data.accessToken),
    };
}
export async function authBoot(audience) {
    try {
        const cookieOnly = getTokenDeliveryMode() === 'cookie';
        if (cookieOnly) {
            clearStoredTokens();
            if (!hasCookieAuthPending()) {
                return { ok: false, reason: 'unauthenticated' };
            }
        }
        const xsrfToken = await fetchCsrfToken();
        const ls = localStorage;
        const ss = sessionStorage;
        const t = cookieOnly
            ? undefined
            : ls.getItem('accessToken') || ss.getItem('accessToken') || undefined;
        const rt = cookieOnly
            ? undefined
            : ls.getItem('refreshToken') || ss.getItem('refreshToken') || undefined;
        const jid = cookieOnly ? undefined : ls.getItem('jti') || ss.getItem('jti') || undefined;
        const remember = cookieOnly ? false : !!ls.getItem('accessToken');
        const exp = t ? decodeExp(t) : 0;
        const needsRefresh = cookieOnly || !t || exp <= now() + SKEW_MS;
        const refreshPayload = rt && jid ? { refreshToken: rt, jti: jid } : undefined;
        const fresh = needsRefresh
            ? await refreshWithCsrf(audience, refreshPayload, xsrfToken)
            : t
                ? { accessToken: t, refreshToken: rt, jti: jid, exp }
                : null;
        if (!fresh)
            return { ok: false, reason: 'unauthenticated' };
        if ('failed' in fresh) {
            if (cookieOnly && !fresh.transient) {
                clearCookieAuthPending();
            }
            return { ok: false, reason: fresh.transient ? 'network' : 'unauthenticated' };
        }
        if (isCookieAuthOk(fresh)) {
            return { ok: true, remember };
        }
        persist(fresh, remember);
        return { ok: true, tokens: fresh, remember };
    }
    catch {
        return { ok: false, reason: 'network' };
    }
}
//# sourceMappingURL=auth-boot.js.map